import type { stats } from "@app/domain";
import type { LeaderboardEntry } from "@app/protocol";
import { useT } from "../i18n/context";
import { trendLook } from "../routes/leaderboardText";
import { Avatar } from "./Avatar";

/** How a row looks: the podium tiers, everyone else, and your own pinned row (design, "Liderlik"). */
interface Tier {
  readonly box: string;
  /** The medal's glow from the left; null off the podium. */
  readonly tint: string | null;
  readonly rank: string;
  readonly ring: string;
  readonly avatar: number;
  readonly name: string;
  readonly points: string;
}

const PODIUM: Readonly<Record<number, Tier>> = {
  1: {
    box: "rounded-[18px] border-gold/90 bg-panel-strong py-[18px] pl-3.5 pr-[18px] shadow-podium",
    tint: "from-gold/22",
    rank: "border-gold/70 bg-gold/18 text-gold",
    ring: "bg-linear-to-b from-gold to-gold-dark",
    avatar: 72,
    name: "text-[20px] font-extrabold",
    points: "text-[30px] text-gold",
  },
  2: {
    box: "rounded-[18px] border-silver/55 bg-panel-strong py-[15px] pl-[13px] pr-4 shadow-podium-low",
    tint: "from-silver/14",
    rank: "border-silver/70 bg-silver/18 text-silver",
    ring: "bg-linear-to-b from-silver to-slate",
    avatar: 60,
    name: "text-[18px] font-extrabold",
    points: "text-[26px] text-silver",
  },
  3: {
    box: "rounded-[18px] border-bronze/55 bg-panel-strong py-[13px] pl-3 pr-3.5 shadow-podium-low",
    tint: "from-bronze/10",
    rank: "border-bronze/70 bg-bronze/18 text-bronze",
    ring: "bg-linear-to-b from-bronze to-bronze-dark",
    avatar: 52,
    name: "text-[16px] font-extrabold",
    points: "text-[22px] text-bronze",
  },
};

const PLAIN: Tier = {
  box: "rounded-[18px] border-ink/14 bg-panel-strong py-3 pl-3 pr-3.5",
  tint: null,
  rank: "border-ink/18 bg-ink/6 text-ink/70",
  ring: "bg-ink/18",
  avatar: 42,
  name: "text-[15px] font-extrabold",
  points: "text-[18px] text-ink",
};

const MINE: Tier = {
  box: "rounded-[16px] border-gold bg-panel-solid px-3.5 py-3 shadow-you",
  tint: null,
  rank: "border-gold/60 bg-gold/15 text-gold",
  ring: "bg-linear-to-b from-gold to-gold-dark",
  avatar: 40,
  name: "text-[15px] font-bold",
  points: "text-[20px] text-ink",
};

const TREND = {
  up: "bg-success/15 text-success",
  down: "bg-danger-soft/15 text-danger-soft",
  new: "bg-gold/15 text-gold",
  flat: "bg-ink/6 text-ink/50",
};

function Trend({ trend }: { trend: LeaderboardEntry["trend"] }) {
  const { t } = useT();
  const look = trendLook(t, trend);
  if (look === null) return null;
  return (
    <span className={`rounded-pill px-2 py-0.5 text-[11px] font-extrabold ${TREND[look.tone]}`}>
      <span aria-hidden="true">{look.glyph}</span>
      <span className="sr-only">{look.name}</span>
    </span>
  );
}

/** One place: rank, ringed picture, name and record, wins and the trend. `mine` is your pinned row. */
export function LeaderboardRow({
  entry,
  mine = false,
  rank = null,
  onOpen,
}: {
  entry: LeaderboardEntry;
  mine?: boolean;
  /** Opens the player's card (review 0017); a hidden player has none. */
  onOpen?: (() => void) | undefined;
  /** The player's rank frame; it takes the medal ring's place (review 0014). */
  rank?: stats.RankFrame | null;
}) {
  const { t } = useT();
  const tier = mine ? MINE : (PODIUM[entry.rank] ?? PLAIN);
  // A player who hid their name (review 0016 decision 92): a stand-in, no picture, no frame.
  const shown = entry.hidden ? { ...entry, name: t("leaderboard.hiddenPlayer") } : entry;
  const box = `relative flex w-full items-center gap-3 overflow-hidden border-[1.5px] text-start ${tier.box}`;
  const inside = (
    <>
      {tier.tint === null ? null : (
        <span
          aria-hidden="true"
          className={`pointer-events-none absolute inset-0 bg-linear-to-r ${tier.tint} to-transparent to-65%`}
        />
      )}
      <span
        className={`relative flex h-[34px] w-[34px] flex-none items-center justify-center rounded-full border-[1.5px] text-[14px] font-extrabold ${tier.rank}`}
      >
        {entry.rank}
      </span>
      {rank === null ? (
        <span className={`relative flex flex-none rounded-full p-0.5 ${tier.ring}`}>
          <Avatar member={shown} size={tier.avatar - 4} />
        </span>
      ) : (
        <Avatar member={shown} size={tier.avatar} rank={rank} />
      )}
      <span className="relative flex min-w-0 flex-1 flex-col gap-[3px]">
        <span className={`truncate ${tier.name}`}>{shown.name}</span>
        <span className="truncate text-[12px] text-ink/70">
          <span aria-hidden="true">
            {t("leaderboard.record", { wins: entry.wins, losses: entry.matches - entry.wins })}
          </span>
          <span className="sr-only">
            {t("leaderboard.recordName", { wins: entry.wins, losses: entry.matches - entry.wins })}
          </span>
        </span>
      </span>
      <span className="relative flex flex-none flex-col items-end gap-1">
        <span className={`font-extrabold leading-none tracking-[-0.02em] ${tier.points}`}>
          {entry.points}
        </span>
        <Trend trend={entry.trend} />
      </span>
    </>
  );
  if (onOpen === undefined || entry.hidden) return <div className={box}>{inside}</div>;
  return (
    <button type="button" aria-haspopup="dialog" className={box} onClick={onOpen}>
      {inside}
    </button>
  );
}
